/**
 * VIN Pulse OS - Authentication Context
 * Real backend authentication via /api/v1/login + /api/v1/users/me
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '../services/api';

export interface User {
  id: string;
  email: string;
  role: string;
  full_name: string;
  is_active: boolean;
  /**
   * Authoritative account status from the backend (login + /me responses).
   * Prefer this over `is_active` for display purposes when present — it
   * distinguishes 'inactive' from 'suspended' and is the source of truth
   * `is_active` is derived from. May be absent on older cached sessions.
   */
  account_status?: 'active' | 'inactive' | 'suspended';
  /** True for onboarded customers who must set their own password before using the app. */
  must_change_password?: boolean;
  // Staff profile fields (migration 027 on vin-pulse-os-api). Admin-managed
  // via the Users module; surfaced here so per-role profile screens can
  // display the real values instead of hardcoded placeholders. Each is
  // optional + nullable so legacy persisted users keep typechecking.
  phone?: string | null;
  employee_id?: string | null;
  department?: string | null;
  territory?: string | null;
  warehouse_code?: string | null;
  notes?: string | null;
}

export interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isSignedIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'vin_pulse_user';

function persistUser(user: User | null): void {
  try {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* localStorage unavailable in some environments — non-fatal */
  }
}

function readPersistedUser(): User | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    bootstrapAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bootstrapAsync = async () => {
    setIsLoading(true);

    // Optimistically rehydrate from cache so the UI doesn't flash login.
    const cached = readPersistedUser();
    if (cached && apiClient.isAuthenticated()) {
      setUser(cached);
      setIsSignedIn(true);
    }

    // If we have a token, verify it server-side and refresh user data.
    if (apiClient.isAuthenticated()) {
      try {
        const fresh = await apiClient.getMe();
        const next: User = {
          id: String(fresh.id),
          email: fresh.email,
          role: fresh.role,
          full_name: fresh.full_name,
          is_active: fresh.is_active,
          account_status: fresh.account_status,
          must_change_password: fresh.must_change_password ?? false,
          phone: fresh.phone ?? null,
          employee_id: fresh.employee_id ?? null,
          department: fresh.department ?? null,
          territory: fresh.territory ?? null,
          warehouse_code: fresh.warehouse_code ?? null,
          notes: fresh.notes ?? null,
        };
        setUser(next);
        setIsSignedIn(true);
        persistUser(next);
      } catch {
        // Token invalid/expired — clear and require re-login.
        apiClient.logout();
        persistUser(null);
        setUser(null);
        setIsSignedIn(false);
      }
    } else {
      persistUser(null);
      setUser(null);
      setIsSignedIn(false);
    }

    setIsLoading(false);
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiClient.login(email.trim(), password);
      const u = result.user;
      const next: User = {
        id: String(u.id),
        email: u.email,
        role: u.role,
        full_name: u.full_name,
        is_active: true,
        account_status: u.account_status,
        must_change_password: u.must_change_password ?? false,
      };
      persistUser(next);
      setUser(next);
      setIsSignedIn(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      setIsSignedIn(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    apiClient.logout();
    persistUser(null);
    setUser(null);
    setIsSignedIn(false);
    setError(null);
  };

  const checkAuth = async () => {
    await bootstrapAsync();
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isSignedIn,
    login,
    logout,
    checkAuth,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Derives whether a user's account should display as "Active".
 *
 * Prefers the authoritative backend `account_status` field when present.
 * Falls back to the legacy `is_active` boolean only when `account_status`
 * is genuinely absent, and treats a missing/undefined `is_active` as
 * active rather than inactive — e.g. right after a fresh login, before
 * the rest of the profile data has settled, the badge should not
 * incorrectly flash "Inactive".
 */
export function isUserActive(
  user: Pick<User, 'is_active' | 'account_status'> | null | undefined
): boolean {
  if (user?.account_status) {
    return user.account_status === 'active';
  }
  return user?.is_active !== false;
}
